import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type {
  FleetPortalContent,
  FleetPortalPage,
  FleetPortalSection,
  ResolvedSite,
} from '@breaker/shared-types';
import type { ThemePreset } from '@breaker/shared-schemas';

/**
 * Website Studio data layer (W4). GET once; edits go through the
 * autosave queue as granular writes and update local editor state —
 * the query cache is only re-pulled on structural changes (add/delete/
 * reorder/publish/discard), never per keystroke.
 */

export interface SiteTree {
  content: FleetPortalContent | null;
  pages: Array<FleetPortalPage & { sections: FleetPortalSection[] }>;
}

export interface PreviewFleetInfo {
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  defaultHighlights: string[];
}

export interface StudioState {
  draft: SiteTree;
  live: SiteTree;
  resolvedDraft: ResolvedSite;
  presets: ThemePreset[];
  dirty: boolean;
  previewFleet: PreviewFleetInfo;
}

export const siteKey = (fleetId: string | null) =>
  ['fleets', fleetId ?? '', 'site'] as const;

export function useSiteStudio(fleetId: string | null) {
  return useQuery({
    queryKey: siteKey(fleetId),
    enabled: Boolean(fleetId),
    queryFn: () => api<StudioState>(`/v1/fleets/${fleetId}/site`),
    staleTime: 30_000,
  });
}

function useInvalidate(fleetId: string | null) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: siteKey(fleetId) });
}

/** Plain senders for the autosave queue (no react-query — the queue
 *  owns debounce/coalescing and error surfacing). */
export function siteSenders(fleetId: string) {
  return {
    brand: (payload: unknown) =>
      api(`/v1/fleets/${fleetId}/site/draft/brand`, { method: 'PUT', body: payload }),
    section: (sectionId: string) => (payload: unknown) =>
      api(`/v1/fleets/${fleetId}/site/sections/${sectionId}`, {
        method: 'PATCH',
        body: payload,
      }),
    page: (pageId: string) => (payload: unknown) =>
      api(`/v1/fleets/${fleetId}/site/pages/${pageId}`, {
        method: 'PATCH',
        body: payload,
      }),
  };
}

export function useCreatePage(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: (title: string) =>
      api<{ page: FleetPortalPage }>(`/v1/fleets/${fleetId}/site/pages`, {
        method: 'POST',
        body: { title },
      }),
    onSuccess: invalidate,
  });
}

export function useDeletePage(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: (pageId: string) =>
      api<void>(`/v1/fleets/${fleetId}/site/pages/${pageId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useReorderPages(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api<void>(`/v1/fleets/${fleetId}/site/pages/reorder`, {
        method: 'POST',
        body: { orderedIds },
      }),
    onSuccess: invalidate,
  });
}

export function useAddSection(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: ({ pageId, envelope }: { pageId: string; envelope: unknown }) =>
      api<{ section: FleetPortalSection }>(
        `/v1/fleets/${fleetId}/site/pages/${pageId}/sections`,
        { method: 'POST', body: envelope },
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteSection(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: (sectionId: string) =>
      api<void>(`/v1/fleets/${fleetId}/site/sections/${sectionId}`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
}

export function useReorderSections(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: ({ pageId, orderedIds }: { pageId: string; orderedIds: string[] }) =>
      api<void>(`/v1/fleets/${fleetId}/site/pages/${pageId}/sections/reorder`, {
        method: 'POST',
        body: { orderedIds },
      }),
    onSuccess: invalidate,
  });
}

export function usePublishSite(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: () =>
      api<{ site: ResolvedSite }>(`/v1/fleets/${fleetId}/site/publish`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: invalidate,
  });
}

export function useDiscardDraft(fleetId: string | null) {
  const invalidate = useInvalidate(fleetId);
  return useMutation({
    mutationFn: () =>
      api<void>(`/v1/fleets/${fleetId}/site/discard`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });
}

export interface SiteImagePresign {
  uploadUrl: string;
  r2Key: string;
  publicUrl: string;
  uploadHeaders: Record<string, string>;
  expiresAt: string;
}

export function useSiteImageInit(fleetId: string | null) {
  return useMutation({
    mutationFn: (body: { kind: 'hero' | 'about' | 'gallery'; mimeType: string; sizeBytes: number }) =>
      api<SiteImagePresign>(`/v1/fleets/${fleetId}/site/image-init`, {
        method: 'POST',
        body,
      }),
  });
}
